import React, { useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useAuth } from '../../context/AuthContext'
import { Button } from '../common/Button'
import { PROD_RELEASE_SCHEDULE } from '../../lib/config/release-config'
import { formatReleaseDate, getDaysUntilDate, getNextProdReleaseDate } from '../../utils/releaseSchedule'

type NavIconProps = {
  className?: string
}

const DashboardIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6V11h-6v9Zm0-18v7h6V2h-6Z" />
  </svg>
)

const TasksIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const KanbanIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M4 5h6v14H4zM14 5h6v8h-6zM14 15h6v4h-6z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const TestingIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M9 12.75 11.25 15 15 9.75" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12a9 9 0 1 1-4.2-7.56" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ReleasesIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M12 3l2.2 4.8 5.3.8-3.8 3.8.9 5.4L12 15.8l-4.6 2.3.9-5.4-3.8-3.8 5.3-.8L12 3Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ManageIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M12 3v4M12 17v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M3 12h4M17 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
)

const QuoteIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="M4 6h16M4 12h10M4 18h7" strokeLinecap="round" />
    <path d="m16 16 2 2 3-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ArrowUpIcon: React.FC<NavIconProps> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
    <path d="m6 11 6-6 6 6M12 5v14" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, signOut, themePreference, updateThemePreference } = useAuth()

  const navItems = useMemo(
    () => [
      { path: '/dashboard', label: 'Dashboard', Icon: DashboardIcon },
      { path: '/tasks', label: 'Tasks', Icon: TasksIcon },
      { path: '/kanban', label: 'Kanban', Icon: KanbanIcon },
      ...(profile?.is_tester || profile?.is_2nd_tester
        ? [{ path: '/testing', label: 'Testing', Icon: TestingIcon }]
        : []),
      ...(profile?.is_uat_release_admin || profile?.is_prod_release_admin
        ? [{ path: '/releases', label: 'Releases', Icon: ReleasesIcon }]
        : []),
      ...(profile?.quote_first ? [{ path: '/quotes', label: 'Quotes', Icon: QuoteIcon }] : []),
      ...(profile?.is_finance_approver ? [{ path: '/finance-approval', label: 'Finance Approval', Icon: QuoteIcon }] : []),
      ...(profile?.role === 'admin' ? [{ path: '/manage', label: 'Manage', Icon: ManageIcon }] : []),
    ],
    [profile?.is_2nd_tester, profile?.is_finance_approver, profile?.is_prod_release_admin, profile?.is_tester, profile?.is_uat_release_admin, profile?.quote_first, profile?.role]
  )

  const nextProdReleaseDate = useMemo(() => getNextProdReleaseDate(PROD_RELEASE_SCHEDULE), [])
  const daysUntilProdRelease = getDaysUntilDate(nextProdReleaseDate)
  const prodReleaseCountdownLabel =
    daysUntilProdRelease === 0 ? 'Today' : `${daysUntilProdRelease} day${daysUntilProdRelease === 1 ? '' : 's'} to go`

  return (
    <div className="min-h-screen bg-stone-300 text-slate-900">
      <header className="sticky top-0 z-40 border-b border-stone-400/70 bg-stone-200/95 backdrop-blur">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 md:px-8">
          <Link to="/dashboard" className="mr-2 whitespace-nowrap text-lg font-semibold tracking-wide text-slate-900">
            iPulse Systems DoD
          </Link>

          <nav className="flex items-center gap-2">
            {navItems.map(({ path, label, Icon }) => {
              const active = location.pathname.startsWith(path)
              return (
                <React.Fragment key={path}>
                  <Link
                    to={path}
                    title={label}
                    aria-label={label}
                    className={clsx(
                      'inline-flex h-11 w-11 items-center justify-center rounded-xl border transition',
                      active
                        ? 'border-slate-700 bg-slate-700 text-stone-100 shadow-sm'
                        : 'border-stone-400 bg-stone-100 text-slate-700 hover:border-stone-500 hover:bg-stone-50'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </Link>
                  <span
                    className={clsx(
                      'whitespace-nowrap text-sm text-slate-600',
                      active ? 'hidden md:inline' : 'hidden lg:inline'
                    )}
                  >
                    {label}
                  </span>
                </React.Fragment>
              )
            })}

          </nav>

          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard?taskList=ready_for_next_release')}
              className="hidden min-w-[205px] rounded-xl border border-stone-400 bg-stone-100 px-4 py-2 text-left shadow-sm transition hover:bg-stone-50 lg:block"
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Next Prod Release</p>
                <p className="text-sm font-semibold text-slate-900">{formatReleaseDate(nextProdReleaseDate)}</p>
              </div>
              <p className="mt-1 text-right text-xs text-slate-600">{prodReleaseCountdownLabel}</p>
            </button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => updateThemePreference(themePreference === 'light' ? 'dark' : 'light')}
              className="h-11 px-4"
            >
              {themePreference === 'light' ? 'Dark mode' : 'Light mode'}
            </Button>
            <div className="rounded-xl border border-stone-400 bg-stone-100 px-4 py-2 text-right shadow-sm">
              <p className="max-w-[220px] truncate text-sm font-medium text-slate-900">
                {profile?.full_name || profile?.email || 'User'}
              </p>
              <p className="text-xs uppercase tracking-wide text-slate-600">{profile?.role || 'unknown'}</p>
            </div>
            <Button
              onClick={() => signOut()}
              variant="ghost"
              size="sm"
              className="h-11 border border-stone-400 bg-stone-100 px-4 text-slate-800 hover:bg-stone-50"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="min-h-[calc(100vh-76px)] px-5 py-6 md:min-h-[calc(100vh-84px)] md:px-8 md:py-8">{children}</main>

      <button
        type="button"
        title="Back to top"
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-30 inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-700 text-stone-100 shadow-lg transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
      >
        <ArrowUpIcon className="h-5 w-5" />
      </button>

    </div>
  )
}
